import { cn } from '@commenda/utils';
import React from 'react';
import { Button } from '../Button';

/**
 * Segment button item configuration.
 */
export interface SegmentButtonItem {
  /** Unique identifier for the button */
  id: string;
  /** Icon element to display */
  icon?: React.ReactNode;
  /** Text label for the button */
  label: string | null;
}

/**
 * Segment button component props.
 */
export interface SegmentButtonProps {
  /** Currently active button ID */
  activeButtonId: string;
  /** Callback when a button is clicked */
  onButtonClick: (buttonId: string) => void;
  /** Array of button items to display */
  items: SegmentButtonItem[];
  /** Size variant for the buttons */
  size?: 'sm' | 'md' | 'lg';
  /** Additional CSS classes */
  className?: string;
}

/**
 * A segmented button group component that displays multiple buttons with one active state.
 * Uses Button.tsx's tertiary variant as base, with active states using hover variant.
 *
 * @example
 * ```tsx
 * const [activeTab, setActiveTab] = useState('files');
 *
 * <SegmentButton
 *   activeButtonId={activeTab}
 *   onButtonClick={setActiveTab}
 *   items={[
 *     { id: 'files', icon: <FileIcon />, label: 'Files' },
 *     { id: 'folders', icon: <FolderIcon />, label: 'Folders' }
 *   ]}
 *   size="md"
 * />
 * ```
 *
 * @param props - Segment button configuration
 * @param props.activeButtonId - Currently active button ID
 * @param props.onButtonClick - Click handler for button selection
 * @param props.items - Array of button items with icon, label, and ID
 * @param props.size - Button size variant (default: 'md')
 * @param props.className - Additional CSS classes
 *
 * @returns A segmented button group component
 */
const SegmentButton = ({
  activeButtonId,
  onButtonClick,
  items,
  size = 'md',
  className,
}: SegmentButtonProps) => {
  return (
    <div
      className={cn(
        'inline-flex rounded-md border border-border-primary overflow-clip divide-x divide-border-primary',
        className
      )}
      role="group"
      aria-label="Segmented button group"
    >
      {items.map((item, index) => {
        const isActive = item.id === activeButtonId;
        const isFirst = index === 0;
        const isLast = index === items.length - 1;

        return (
          <Button
            key={item.id}
            variant="tertiary"
            size={size}
            onClick={() => onButtonClick(item.id)}
            aria-pressed={isActive}
            className={cn(
              // Base styling
              'rounded-none shadow-none bg-bg-primary',
              // First button styling
              isFirst && 'rounded-l-md',
              // Last button styling
              isLast && 'rounded-r-md',
              // Active state styling (uses hover variant)
              isActive &&
                'bg-bg-secondary hover:bg-bg-secondary text-text-primary',
              // Hover state for inactive buttons
              !isActive && 'hover:bg-bg-secondary hover:text-text-primary'
            )}
          >
            {item.icon}
            {item.label && <span>{item.label}</span>}
          </Button>
        );
      })}
    </div>
  );
};

SegmentButton.displayName = 'SegmentButton';

export { SegmentButton };
